import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const ZipData = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [zips, setZips] = useState([]);
  const [editingZip, setEditingZip] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newZip, setNewZip] = useState({ zipcode: '', city: '', zone: '', approx_miles: '' });

  useEffect(() => { fetchData(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [zipRes, custRes] = await Promise.all([
        API.get(`/customers/${customerId}/zip-data`),
        API.get('/customers')
      ]);
      setZips(zipRes.data);
      const cust = custRes.data.find(c => c.customer_id === parseInt(customerId));
      if (cust) setCustomerName(cust.name);
    } catch {
      toast.error('Failed to load ZIP data');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (zip) => { setEditingZip(zip.zipcode); setEditRow({ ...zip }); };
  const cancelEdit = () => { setEditingZip(null); setEditRow({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await API.put(`/customers/${customerId}/zip-data/${editingZip}`, editRow);
      setZips(prev => prev.map(z => z.zipcode === editingZip ? { ...editRow } : z));
      setEditingZip(null);
      toast.success('ZIP updated');
    } catch {
      toast.error('Failed to save ZIP');
    } finally {
      setSaving(false);
    }
  };

  const deleteZip = async (zipcode) => {
    if (!window.confirm('Delete this ZIP from this customer?')) return;
    try {
      await API.delete(`/customers/${customerId}/zip-data/${zipcode}`);
      setZips(prev => prev.filter(z => z.zipcode !== zipcode));
      toast.success('ZIP removed');
    } catch {
      toast.error('Failed to delete ZIP');
    }
  };

  const addZip = async () => {
    if (!newZip.zipcode || !newZip.zone) return toast.error('ZIP code and zone are required');
    if (newZip.zipcode.length !== 5) return toast.error('ZIP code must be 5 digits');
    setSaving(true);
    try {
      await API.post(`/customers/${customerId}/zip-data`, newZip);
      await fetchData();
      setShowAddModal(false);
      setNewZip({ zipcode: '', city: '', zone: '', approx_miles: '' });
      toast.success('ZIP added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add ZIP');
    } finally {
      setSaving(false);
    }
  };

  const filtered = zips.filter(z =>
    z.zipcode.includes(search) ||
    (z.city || '').toLowerCase().includes(search.toLowerCase()) ||
    (z.zone || '').toLowerCase().includes(search.toLowerCase())
  );

  const thStyle = {
    padding: '10px 8px', background: '#1A2A3A', color: 'white',
    fontSize: '12px', fontWeight: '500', textAlign: 'left',
    borderRight: '1px solid #F5A800'
  };

  const tdStyle = {
    padding: '7px 8px', borderBottom: '1px solid #E2E8F0',
    borderRight: '1px solid #f0f0f0', fontSize: '13px'
  };

  const inputStyle = {
    padding: '4px 6px', border: '1px solid #E8B84B',
    borderRadius: '4px', fontSize: '12px', outline: 'none', background: '#FDF4DD'
  };

  const modalInputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0',
    borderRadius: '6px', fontSize: '14px', outline: 'none'
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600' }}>ZIP Data</h1>
          <p style={{ color: '#5B6B7F', fontSize: '14px' }}>
            Customer: <strong>{customerName}</strong>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/dashboard')} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
            ← Dashboard
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 16px' }}>
            + Add ZIP
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
        <input
          style={{ padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none', width: '280px' }}
          placeholder="Search by ZIP, city, or zone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={{ marginLeft: '12px', fontSize: '13px', color: '#5B6B7F' }}>
          {filtered.length} of {zips.length} ZIPs
        </span>
      </div>

      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0 }}>
              <tr>
                <th style={thStyle}>ZIP</th>
                <th style={thStyle}>City</th>
                <th style={thStyle}>Zone</th>
                <th style={thStyle}>Miles</th>
                <th style={{ ...thStyle, borderRight: 'none' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(zip => {
                const isEditing = editingZip === zip.zipcode;
                const row = isEditing ? editRow : zip;
                return (
                  <tr key={zip.zipcode} style={{ background: isEditing ? '#FDF4DD' : 'white' }}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{row.zipcode}</td>
                    <td style={tdStyle}>
                      {isEditing
                        ? <input style={{ ...inputStyle, width: '140px' }} value={row.city || ''} onChange={e => setEditRow(prev => ({ ...prev, city: e.target.value }))} />
                        : row.city}
                    </td>
                    <td style={tdStyle}>
                      {isEditing
                        ? <input style={{ ...inputStyle, width: '40px' }} value={row.zone || ''} onChange={e => setEditRow(prev => ({ ...prev, zone: e.target.value.toUpperCase() }))} maxLength={1} />
                        : <span style={{ fontWeight: '600' }}>{row.zone}</span>}
                    </td>
                    <td style={tdStyle}>
                      {isEditing
                        ? <input style={{ ...inputStyle, width: '60px' }} type="number" value={row.approx_miles || ''} onChange={e => setEditRow(prev => ({ ...prev, approx_miles: e.target.value }))} />
                        : row.approx_miles}
                    </td>
                    <td style={{ ...tdStyle, borderRight: 'none', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={saving} className="fcc-press" style={{ background: '#15803D', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>Save</button>
                          <button onClick={cancelEdit} className="fcc-press" style={{ background: '#5B6B7F', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(zip)} className="fcc-press" style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>Edit</button>
                          <button onClick={() => deleteZip(zip.zipcode)} className="fcc-press" style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add ZIP Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="fcc-modal-pop" style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>Add ZIP Code</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>ZIP Code * (5 digits)</label>
                <input style={modalInputStyle} placeholder="e.g. 33508" maxLength={5} value={newZip.zipcode} onChange={e => setNewZip(prev => ({ ...prev, zipcode: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>City</label>
                <input style={modalInputStyle} placeholder="e.g. BRANDON" value={newZip.city} onChange={e => setNewZip(prev => ({ ...prev, city: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Zone * (single letter)</label>
                <input style={modalInputStyle} placeholder="e.g. B" maxLength={1} value={newZip.zone} onChange={e => setNewZip(prev => ({ ...prev, zone: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Approx Drive Miles</label>
                <input style={modalInputStyle} type="number" placeholder="e.g. 20" value={newZip.approx_miles} onChange={e => setNewZip(prev => ({ ...prev, approx_miles: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowAddModal(false); setNewZip({ zipcode: '', city: '', zone: '', approx_miles: '' }); }} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '9px 18px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>Cancel</button>
              <button onClick={addZip} disabled={saving} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '9px 20px' }}>
                {saving ? 'Adding...' : 'Add ZIP'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZipData;